<template>
  <DropdownHybrid ref="dropdown" v-model="modelValue" :menu-props="menuProps">
    <template #activator>
      <div ref="currentElement" />
    </template>

    <template #sheet-prepend>
      <div class="px-4 py-3">
        <h4
          class="text-h5 font-weight-semibold mb-1"
        >
          {{ t('page.bookmarks.options.title') }}
        </h4>
        <p class="text-body-2 text-neutral600 mb-0">
          {{ bookmark.title ?? bookmark.domain }}
        </p>
      </div>
    </template>

    <v-list bg-color="neutral25" class="py-3">
      <v-list-item
        v-for="option in options" :key="option.key"
        :class="option.destructive ? 'text-red400' : 'text-neutral900'"
        @click="onSelect(option.value)"
      >
        <template #prepend>
          <component :is="option.icon" :weight="option.iconWeight" class="text-h5 mr-3" />
        </template>

        <v-list-item-title class="font-weight-medium">
          {{ t(option.text) }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </DropdownHybrid>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  PhListPlus, PhPencil, PhPlus,
  PhChatCenteredText, PhExport, PhTagSimple, PhTrash, PhPushPin,
} from '@phosphor-icons/vue'
import {
  canPerformItemAction,
  buildPermissionContext,
  CollectionItemAction,
} from '@/utils/permissions/collection-item'

const props = defineProps({
  modelValue: Boolean,
  // Accepts both Bookmark and CollectionItem (when resourceType is 'bookmark')
  bookmark: {
    type: Object,
    required: true,
  },
  userId: {
    type: String,
    default: null,
  },
  membership: {
    type: Object,
    default: null,
  },
  publicMode: {
    type: Boolean,
    default: false,
  },
  isGridLayout: Boolean,
  withPrimaryAction: Boolean,
  withSecondaryAction: Boolean,
})

const emit = defineEmits([
  'update:modelValue',
  'comments',
  'save',
  'add-to-collection',
  'tags',
  'edit',
  'delete',
  'share',
  'pin',
  'unpin',
])

const { t } = useI18n()
const dropdown = ref(null)

const currentElement = ref(null)

const modelValue = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  },
})

// Build permission context for checking actions (includes public collection awareness)
const permissionContext = computed(() => {
  return buildPermissionContext({
    membership: props.membership,
    item: props.bookmark,
    userId: props.userId,
    collection: props.publicMode ? { isPublic: true } : undefined,
  })
})

// Permission check helpers
const canEdit = computed(() => canPerformItemAction(permissionContext.value, CollectionItemAction.EDIT))
const canDelete = computed(() => canPerformItemAction(permissionContext.value, CollectionItemAction.DELETE))
const canManageTags = computed(() => canPerformItemAction(permissionContext.value, CollectionItemAction.MANAGE_TAGS))
const canPin = computed(() => canPerformItemAction(permissionContext.value, CollectionItemAction.PIN))
const canShare = computed(() => canPerformItemAction(permissionContext.value, CollectionItemAction.SHARE))
const canCopy = computed(() => canPerformItemAction(permissionContext.value, CollectionItemAction.COPY_TO_COLLECTION))

const options = computed(() => {
  const optionsList = []
  const { isCollectionBookmark } = props.bookmark

  if (props.publicMode) {
    if (canShare.value) {
      optionsList.push({
        text: 'cta.share_bookmark',
        value: 'share',
        icon: PhExport,
        order: 1,
      })
    }
    return optionsList
  }

  if (props.withPrimaryAction) {
    optionsList.push({
      text: isCollectionBookmark ? 'common.comments' : 'common.notes',
      value: 'comments',
      icon: PhChatCenteredText,
      order: 1,
    })
  }

  if (props.withSecondaryAction) {
    if (isCollectionBookmark) {
      optionsList.push({
        text: 'cta.add_to_bookmarks',
        value: 'save',
        icon: PhPlus,
        order: 2,
      })
    } else {
      optionsList.push({
        text: 'cta.add_to_collection',
        value: 'add-to-collection',
        icon: PhListPlus,
        order: 2,
      })
    }
  }

  if (canManageTags.value) {
    optionsList.push({
      text: 'cta.manage_tags',
      value: 'tags',
      icon: PhTagSimple,
      order: 5,
    })
  }

  if (canEdit.value) {
    optionsList.push({
      text: 'cta.edit_bookmark',
      value: 'edit',
      icon: PhPencil,
      order: 3,
    })
  }

  if (canPin.value) {
    if (props.bookmark.pinPosition != null) {
      optionsList.push({
        text: 'cta.remove_pin',
        value: 'unpin',
        icon: PhPushPin,
        iconWeight: 'fill',
        order: 5.5,
      })
    } else {
      optionsList.push({
        text: 'cta.pin_item',
        value: 'pin',
        icon: PhPushPin,
        order: 5.5,
      })
    }
  }

  if (canDelete.value) {
    optionsList.push({
      text: 'cta.delete_bookmark',
      value: 'delete',
      destructive: true,
      icon: PhTrash,
      order: 6,
    })
  }

  if (canShare.value) {
    optionsList.push({
      text: 'cta.share_bookmark',
      value: 'share',
      icon: PhExport,
      order: 4,
    })
  }

  return optionsList.sort((a, b) => a.order - b.order)
})



const menuProps = computed(() => {
  const opts = {
    offset: [-30, 0],
    activator: currentElement.value,
    openOnClick: false,
    closeOnContentClick: false,
    contentClass: 'bookmark-options-dropdown',
    locationStrategy: 'connected',
    location: 'bottom',
    origin: 'top',
    rounded: 'lg',
    disabled: props.disabled,
    contained: false,
  }

  if (props.isGridLayout) {
    return {
      ...opts,
      location: 'bottom',
    }
  }

  return {
    ...opts,
    // offset: [16, 0],
    minWidth: 270,
  }
})

function onSelect(val) {
  dropdown.value?.close()
  emit(val, props.bookmark)
}
</script>

<style lang="scss" scoped>
.bookmark-options-dropdown {
  max-height: 600px !important;
}
</style>
